import type { TownPropTemplate, TownPropPalette } from './townPropTemplates';
import { buildTownHouseBody } from './townHouseTexture';
import { buildTownLanternBody, buildHangingLanternBody } from './townLanternTexture';
import { buildQuayCargoBody } from './townDockTexture';
import { buildTrainerHallBody } from './townFacadeTexture';
import { buildMarketStallBody } from './townShopTexture';
import { buildHarborShopfrontBody, buildLanternHallBody } from './townHarborFrontageTexture';
import { buildBracketLanternBody, buildShopSignBody } from './townMountedFixtureTexture';
import { buildWorldPropBody } from './worldPropTexture';
import { buildTownCivicBody } from './townCivicTexture';
import { buildTownProvisionBody } from './townProvisionTexture';
import { buildOpeningAreaPropBody } from './openingAreaTexture';
import { buildTempleLandmarkBody } from './templeLandmarkTexture';
import { buildSunkenLandmarkBody } from './sunkenLandmarkTexture';
import { buildFenwatchBeaconBody } from './fenwatchBeaconTexture';
import { buildBoundaryPropBody } from './boundaryPropTexture';
import { buildQuaysideDetailBody } from './quaysideDetailTexture';
import { buildFacadePanelBody } from './facadePanelTexture';
import { buildFacadeFixtureBody } from './facadeFixtureTexture';
import { buildHarborBoatBody } from './harborBoatTexture';
import { buildHarborEquipmentBody } from './harborEquipmentTexture';
import { buildHarborFishingBody } from './harborFishingTexture';
import { buildCoilPropBody } from './coilPropTexture';
import { buildCavernPropBody } from './cavernPropTexture';
import { buildCoastalDetailBody } from './coastalDetailTexture';
import { buildShelfFungusBody } from './shelfFungusTexture';
import { buildHushPropBody } from './hushPropTexture';
import { isRecipeFamilyTemplate, recipeFamilyFor } from './recipeFamilies';
import { buildRouteTemplateBody } from '../../../../shared/scene/routeProps/runtimeFacade';

/** One named-template dispatch within the existing prop texture factory. */
export function buildTownTemplateBody(template: TownPropTemplate, palette: TownPropPalette, context?: string): string {
  if (isRecipeFamilyTemplate(template)) return recipeFamilyFor(template)!.buildBody(template, palette);
  switch (template) {
  case 'gable_inn':
  case 'cross_gable_house':
  case 'timber_hall': return buildTownHouseBody(template, palette);
  case 'crook_lantern':
  case 'twin_lantern':
  case 'storm_lantern': return buildTownLanternBody(template, palette);
  case 'quay_cargo': return buildQuayCargoBody(palette);
  case 'trainer_hall': return buildTrainerHallBody(palette);
  case 'market_stall': return buildMarketStallBody(palette);
  case 'hanging_lantern': return buildHangingLanternBody(palette);
  case 'harbor_shopfront': return buildHarborShopfrontBody(palette);
  case 'lantern_hall': return buildLanternHallBody(palette);
  case 'bracket_lantern': return buildBracketLanternBody(palette);
  case 'shop_sign': return buildShopSignBody(palette);
  case 'quayside_bench':
  case 'flower_planter':
  case 'notice_board':
  case 'ferry_waymarker': return buildTownCivicBody(template, palette);
  case 'supply_handcart':
  case 'net_drying_rack':
  case 'provision_barrels':
  case 'rope_well': return buildTownProvisionBody(template, palette);
  case 'vault_archway':
  case 'vault_ossuary':
  case 'hearth_well_frame': return buildOpeningAreaPropBody(template, palette);
  case 'temple_sanctum_front':
  case 'temple_inscribed_relief':
  case 'temple_attendant_statue': return buildTempleLandmarkBody(template, palette);
  case 'sunken_aqueduct_front':
  case 'sunken_broken_arch':
  case 'sunken_coral_reliquary': return buildSunkenLandmarkBody(template, palette);
  case 'fenwatch_beacon_frame': return buildFenwatchBeaconBody(palette);
  case 'timber_fence_panel':
  case 'timber_fence_gate':
  case 'stone_wall_segment':
  case 'stone_pier_post': return buildBoundaryPropBody(template, palette);
  case 'rope_rail_segment':
  case 'harbor_bollard':
  case 'open_supply_crate': return buildQuaysideDetailBody(template, palette, context);
  case 'market_goods_table': return buildQuaysideDetailBody(template, palette);
  case 'plaster_wall_bay':
  case 'timber_wall_bay':
  case 'arched_entry_door':
  case 'plank_entry_door': return buildFacadePanelBody(template, palette);
  case 'shuttered_window':
  case 'diamond_leaded_window':
  case 'striped_shop_awning':
  case 'scalloped_door_canopy': return buildFacadeFixtureBody(template, palette);
  case 'moored_fishing_skiff':
  case 'furled_coastal_sloop': return buildHarborBoatBody(template, palette);
  case 'harbor_cargo_hoist':
  case 'sail_repair_rack': return buildHarborEquipmentBody(template, palette);
  case 'crab_pot_stack':
  case 'fish_market_counter':
  case 'life_ring_post':
  case 'coiled_rope_basket': return buildHarborFishingBody(template, palette);
  case 'eldritch_altar':
  case 'cracked_colonnade':
  case 'void_brazier':
  case 'runic_slab': return buildCoilPropBody(template, palette);
  case 'hanging_root':
  case 'ceiling_stalactite':
  case 'spore_pod':
  case 'hollow_root_column': return buildCavernPropBody(template, palette);
  case 'tidal_buoy':
  case 'drowned_cobble': return buildCoastalDetailBody(template, palette);
  case 'shelf_fungus': return buildShelfFungusBody(palette);
  case 'paper_boat':
  case 'chalk_skip_marks':
  case 'keepsake_shelf': return buildHushPropBody(template, palette);
  case 'coastal_fallen_log':
  case 'coastal_split_log':
  case 'moor_fallen_log':
  case 'moor_dead_bracken':
  case 'moor_wind_bracken':
  case 'moor_sparse_bracken':
  case 'causeway_tide_pool':
  case 'causeway_silt_pool':
  case 'causeway_shingle_pool':
  case 'moor_mist_shelf':
  case 'moor_split_mist_shelf':
  case 'moor_low_mist_shelf': return buildRouteTemplateBody(template, palette);
  case 'layered_outcrop':
  case 'crystal_cluster':
  case 'frost_shards':
  case 'desert_cairn':
  case 'moss_log':
  case 'root_bridge':
  case 'ruin_arch':
  case 'standing_stones':
  case 'giant_mushroom':
  case 'reed_bed': return buildWorldPropBody(template, palette);
  }
}
